import { element, heading, table } from "@groundspark/browser-ui";

export interface DailyActivity {
  readonly date: string;
  readonly count: number;
}

export interface HistoryLabels {
  readonly title: string;
  /** What is counted, such as `streets`. */
  readonly unit: string;
  /** What a count means, such as `streets leafleted`. */
  readonly measure: string;
}

/** Bars stay thin however few days there are. */
const MAXIMUM_BAR_WIDTH = 24;

const numbers = new Intl.NumberFormat("en-GB");
const dates = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeZone: "UTC" });
const shortDates = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

export function coverageChart(
  completed: number,
  total: number,
  unit: string,
  action: string,
): HTMLElement {
  const remaining = total - completed;
  const chart = svg("svg", { viewBox: "0 0 200 200", class: "activity-pie" });
  chart.setAttribute("role", "img");
  chart.setAttribute(
    "aria-label",
    `${numbers.format(completed)} ${unit} ${action}; ${numbers.format(remaining)} remaining`,
  );
  chart.append(
    svg("circle", { cx: "100", cy: "100", r: "96", class: "activity-pie__remaining" }),
  );
  if (completed === total && total > 0) {
    chart.append(
      svg("circle", { cx: "100", cy: "100", r: "96", class: "activity-pie__completed" }),
    );
  } else if (completed > 0) {
    const angle = (completed / total) * Math.PI * 2;
    const x = 100 + 96 * Math.sin(angle);
    const y = 100 - 96 * Math.cos(angle);
    chart.append(
      svg("path", {
        d: `M 100 100 L 100 4 A 96 96 0 ${angle > Math.PI ? 1 : 0} 1 ${x} ${y} Z`,
        class: "activity-pie__completed",
      }),
    );
  }
  return element(
    "div",
    "activity-coverage",
    chart,
    element(
      "div",
      "activity-coverage__copy",
      element("p", "activity-total", numbers.format(completed)),
      element("p", null, `${unit} ${action}`),
      total === 0
        ? element("p", "muted", "No targets in these projects")
        : element(
            "p",
            "muted",
            `${numbers.format(total)} total · ${Math.round((completed / total) * 100)}% ${action}`,
          ),
      element(
        "ul",
        "activity-legend",
        legend(
          action.charAt(0).toUpperCase() + action.slice(1),
          completed,
          "activity-legend__completed",
        ),
        legend("Remaining", remaining, "activity-legend__remaining"),
      ),
    ),
  );
}

export function historyChart(
  history: readonly DailyActivity[],
  { title, unit, measure }: HistoryLabels,
): HTMLElement {
  const chart = svg("svg", { viewBox: "0 0 720 240", class: "activity-history" });
  chart.setAttribute("role", "img");
  const total = history.reduce((sum, day) => sum + day.count, 0);
  const first = history[0];
  const last = history.at(-1);
  chart.setAttribute(
    "aria-label",
    `${numbers.format(total)} ${measure}${first === undefined || last === undefined ? "" : ` from ${dates.format(new Date(first.date))} to ${dates.format(new Date(last.date))}`}. Daily counts are available in the table below.`,
  );
  const maximum = Math.max(1, ...history.map((day) => day.count));
  const top = Math.max(1, Math.ceil(maximum / 4) * 4);
  const plot = { left: 64, top: 16, width: 636, height: 176 };
  for (let tick = 0; tick <= 4; tick++) {
    const y = plot.top + plot.height - (tick / 4) * plot.height;
    chart.append(
      svg("line", {
        x1: String(plot.left),
        x2: String(plot.left + plot.width),
        y1: String(y),
        y2: String(y),
        class: "activity-history__grid",
      }),
      svgText(numbers.format((tick / 4) * top), plot.left - 10, y + 4, "end"),
    );
  }
  const slot = plot.width / Math.max(1, history.length);
  const barWidth = Math.min(slot * 0.8, MAXIMUM_BAR_WIDTH);
  history.forEach((day, index) => {
    const height = (day.count / top) * plot.height;
    const bar = svg("rect", {
      x: String(plot.left + index * slot + (slot - barWidth) / 2),
      y: String(plot.top + plot.height - height),
      width: String(barWidth),
      height: String(height),
      class: "activity-history__bar",
    });
    const title = svg("title");
    title.textContent = `${dates.format(new Date(day.date))}: ${numbers.format(day.count)} ${unit}`;
    bar.append(title);
    chart.append(bar);
  });
  const indices = [
    ...new Set([
      0,
      Math.floor((history.length - 1) / 3),
      Math.floor(((history.length - 1) * 2) / 3),
      history.length - 1,
    ]),
  ];
  for (const index of indices) {
    const day = history[index];
    if (day === undefined) continue;
    chart.append(
      svgText(
        shortDates.format(new Date(day.date)),
        plot.left + (index + 0.5) * slot,
        222,
        index === 0 ? "start" : index === history.length - 1 ? "end" : "middle",
      ),
    );
  }
  const details = element(
    "details",
    "activity-history__details",
    element("summary", null, "View daily counts"),
    table(
      ["Date (UTC)", unit],
      history.map((day) => ({
        cells: [dates.format(new Date(day.date)), numbers.format(day.count)],
      })),
    ),
  );
  return element(
    "section",
    "activity-history-section",
    heading(3, title),
    element("p", "muted", `${numbers.format(total)} ${measure} in this period · dates in UTC`),
    chart,
    total === 0 ? element("p", "muted", "No activity in this period") : null,
    details,
  );
}

function legend(label: string, count: number, className: string): HTMLElement {
  return element("li", null, element("span", className), `${label}: ${numbers.format(count)}`);
}

function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attributes: Readonly<Record<string, string>> = {},
): SVGElementTagNameMap[K] {
  const result = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [key, value] of Object.entries(attributes)) result.setAttribute(key, value);
  return result;
}

function svgText(value: string, x: number, y: number, anchor: string): SVGTextElement {
  const result = svg("text", {
    x: String(x),
    y: String(y),
    "text-anchor": anchor,
    class: "activity-history__label",
  });
  result.textContent = value;
  return result;
}
